// ink-component
import React, { ReactElement } from 'react';
import styled, { css, ThemeProvider } from 'styled-components';

import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { colorTokens } from '../../../../tokens/globals/tokens';

import NewTableOrderableRow from '../NewTableOrderableRow/NewTableOrderableRow';

export interface NewTableOrderableBodyProps extends React.TableHTMLAttributes<HTMLTableSectionElement> {
  /** Unique ID for this orderable table */
  id: string;
  /** Array of objects corresponding to the data being fed into the table */
  data: Array<Record<string, unknown>>;
  /** Optional callback that returns data about the new order after a row has been moved  */
  onDragEnd?: any;
}

/* @internal */
export const StyledOrderableBody = styled.tbody`
  tr[data-rbd-placeholder-context-id] {
    position: relative;

    &::after {
      content: ' ';
      display: block;
      box-sizing: border-box;
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      bottom: 0;
      width: 100%;
      border-right: 1px solid ${colorTokens.GlobalBrandGray30};
      border-left: 1px solid ${colorTokens.GlobalBrandGray30};
      z-index: 0;
    }
  }

  // Only for OrderableTables to make it look like there's a backdrop on drag
  ${({ theme }) =>
    theme.isDraggingOver &&
    css`
      background: ${colorTokens.GlobalBrandGray10};
    `}
`;

/**
 * @group NewTable
 * @purpose Reorder table rows within this body via drag-and-drop or keyboard
 * @categories table
 * @HTMLType HTMLTableBody
 */
const NewTableOrderableBody = (props: NewTableOrderableBodyProps) => {
  const { style, className, children, data, ...otherProps } = props;
  const childrenArray = React.Children.toArray(children) as ReactElement[];
  const [tableRows, setTableRows] = React.useState<ReactElement[]>(childrenArray);

  // This hook is necessary for the use case of an OrderableTable with sortable headers
  React.useEffect(() => {
    setTableRows(React.Children.toArray(children) as ReactElement[]);
  }, [children, data]);

  const reorder = (list: any[], startIndex: number, endIndex: number): any[] => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const reorderRows = result => {
    // dropped outside the list
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }

    // no movement
    if (result.destination.index === result.source.index) {
      return;
    }

    const newTableRows = reorder(tableRows, result.source.index, result.destination.index);
    setTableRows(newTableRows);

    // Optional callback - return reordered data to user
    if (otherProps.onDragEnd) {
      const newTableData = reorder(data, result.source.index, result.destination.index);
      otherProps.onDragEnd({
        data: newTableData,
        active: data[result.source.index],
        beforeIndex: result.source.index,
        afterIndex: result.destination.index,
      });
    }
  };

  return (
    <DragDropContext onDragEnd={reorderRows}>
      <Droppable droppableId={otherProps.id}>
        {(droppableProvided, snapshot) => (
          <ThemeProvider
            theme={tableTheme => ({
              ...tableTheme,
              isDraggingOver: !!snapshot.isDraggingOver,
            })}
          >
            <StyledOrderableBody {...otherProps} ref={droppableProvided.innerRef}>
              {tableRows.map((row, rowIndex) => {
                const { index, ...otherRowProps } = row.props;
                return (
                  <NewTableOrderableRow {...otherRowProps} rowIndex={rowIndex} key={row.key} uniqueKey={row.key}>
                    {otherRowProps.children}
                  </NewTableOrderableRow>
                );
              })}
              {droppableProvided.placeholder}
            </StyledOrderableBody>
          </ThemeProvider>
        )}
      </Droppable>
    </DragDropContext>
  );
};

NewTableOrderableBody.defaultProps = {
  id: undefined,
  data: undefined,
  onDragEnd: undefined,
};

NewTableOrderableBody.displayName = 'NewTable.OrderableBody';

export default NewTableOrderableBody;
